/**
 * ═══════════════════════════════════════════════════════════════════════════════
 *  DEXEL GPU ENGINE - WebGPU Accelerated Material Removal
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * WebGPU compute shader ile paralel dexel hesaplamaları.
 * CPU'da binlerce hücre sırayla işlenirken, GPU'da hepsi aynı anda işlenir.
 *
 * Performance: 10-100x faster than CPU for large grids
 */

import * as THREE from 'three';

export interface DexelGPUConfig {
  resX: number;
  resY: number;
  cellSize: number;
  bbox: {
    minX: number; maxX: number;
    minY: number; maxY: number;
    minZ: number; maxZ: number;
  };
  silindirYonModu: number; // 0: Freze, 1: Torna
}

export interface GPUToolSample {
  x: number;
  y: number;
  z: number;
  toolRadius: number;
  toolType: number; // 0: flat, 1: ball, 2: bull
  cornerRadius: number;
  toolAxisI: number;
  toolAxisJ: number;
  toolAxisK: number;
  toolLength: number;
}

const MAX_SPANS = 8;
const SPAN_SIZE = 2; // [min, max]

// WebGPU Compute Shader - Material Removal
const DEXEL_COMPUTE_SHADER = /* wgsl */`
struct Uniforms {
  resX: u32,
  resY: u32,
  cellSize: f32,
  bboxMinX: f32,
  bboxMinY: f32,
  bboxMinZ: f32,
  bboxMaxZ: f32,
  bboxMaxX: f32,
  toolX: f32,
  toolY: f32,
  toolZ: f32,
  toolRadius: f32,
  toolType: u32,
  cornerRadius: f32,
  toolAxisI: f32,
  toolAxisJ: f32,
  toolAxisK: f32,
  toolLength: f32,
  _pad0: f32,
  _pad1: f32,
}

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read_write> spans: array<f32>;
@group(0) @binding(2) var<storage, read_write> spanCounts: array<u32>;
@group(0) @binding(3) var<storage, read_write> heightMap: array<f32>;
@group(0) @binding(4) var<storage, read_write> dirtyMask: array<u32>;

const MAX_SPANS: u32 = 8u;
const SPAN_SIZE: u32 = 2u;

@compute @workgroup_size(16, 16)
fn processToolPosition(@builtin(global_invocation_id) gid: vec3<u32>) {
  let ix = gid.x;
  let iy = gid.y;

  if (ix >= uniforms.resX || iy >= uniforms.resY) {
    return;
  }

  let idx = iy * uniforms.resX + ix;

  // World position of cell center
  let worldX = uniforms.bboxMinX + (f32(ix) + 0.5) * uniforms.cellSize;
  let worldY = uniforms.bboxMinY + (f32(iy) + 0.5) * uniforms.cellSize;

  // Distance from tool center
  let dx = worldX - uniforms.toolX;
  let dy = worldY - uniforms.toolY;
  let dist2D_sq = dx * dx + dy * dy;
  let R2 = uniforms.toolRadius * uniforms.toolRadius;

  // Outside tool radius - skip
  if (dist2D_sq > R2) {
    return;
  }

  let dist2D = sqrt(dist2D_sq);

  // Calculate effective Z based on tool type
  var effectiveZ = uniforms.toolZ;

  // Ball-end mill
  if (uniforms.toolType == 1u) {
    let R = uniforms.toolRadius;
    if (dist2D < R) {
      let heightOffset = sqrt(R * R - dist2D_sq);
      effectiveZ = uniforms.toolZ - R + heightOffset;
    }
  }
  // Bull-nose mill
  else if (uniforms.toolType == 2u) {
    let R = uniforms.toolRadius;
    let cornerR = uniforms.cornerRadius;
    let flatRadius = R - cornerR;
    if (dist2D > flatRadius && dist2D < R) {
      let distFromCorner = dist2D - flatRadius;
      if (distFromCorner < cornerR) {
        let zOffset = sqrt(cornerR * cornerR - distFromCorner * distFromCorner) - cornerR;
        effectiveZ = uniforms.toolZ + zOffset;
      }
    }
  }

  let absI = abs(uniforms.toolAxisI);
  let absJ = abs(uniforms.toolAxisJ);
  let absK = abs(uniforms.toolAxisK);

  var cutMin: f32;
  var cutMax: f32;

  if (absK >= absI && absK >= absJ && absK >= 0.3) {
    if (uniforms.toolAxisK >= 0.0) {
      if (effectiveZ >= uniforms.bboxMaxZ) { return; }
      cutMin = max(effectiveZ, uniforms.bboxMinZ);
      cutMax = uniforms.bboxMaxZ;
    } else {
      if (effectiveZ <= uniforms.bboxMinZ) { return; }
      cutMin = uniforms.bboxMinZ;
      cutMax = min(effectiveZ, uniforms.bboxMaxZ);
    }
  } else if (absI >= absJ && absI >= 0.3) {
    let halfLen = uniforms.toolLength * 0.5;
    cutMin = max(effectiveZ - halfLen, uniforms.bboxMinZ);
    cutMax = min(effectiveZ + halfLen, uniforms.bboxMaxZ);
  } else if (absJ >= 0.3) {
    let halfLen = uniforms.toolLength * 0.5;
    cutMin = max(effectiveZ - halfLen, uniforms.bboxMinZ);
    cutMax = min(effectiveZ + halfLen, uniforms.bboxMaxZ);
  } else {
    let halfLen = uniforms.toolLength * 0.5;
    cutMin = max(effectiveZ - halfLen, uniforms.bboxMinZ);
    cutMax = min(effectiveZ + halfLen, uniforms.bboxMaxZ);
  }

  if (cutMin >= cutMax) { return; }

  // Subtract span from column
  let spanCount = spanCounts[idx];
  let offset = idx * MAX_SPANS * SPAN_SIZE;

  if (spanCount == 0u) {
    heightMap[idx] = uniforms.bboxMinZ;
    return;
  }

  // Process each span
  for (var s: u32 = 0u; s < spanCount; s++) {
    let spanOffset = offset + s * SPAN_SIZE;
    let spanMin = spans[spanOffset];
    let spanMax = spans[spanOffset + 1u];

    // No overlap
    if (cutMax <= spanMin || cutMin >= spanMax) {
      continue;
    }

    // Complete removal
    if (cutMin <= spanMin && cutMax >= spanMax) {
      spans[spanOffset] = 0.0;
      spans[spanOffset + 1u] = 0.0;
      spanCounts[idx] = spanCounts[idx] - 1u;
      dirtyMask[idx] = 1u;
      continue;
    }

    // Cut from top
    if (cutMin > spanMin && cutMax >= spanMax) {
      spans[spanOffset + 1u] = cutMin;
      dirtyMask[idx] = 1u;
      continue;
    }

    // Cut from bottom
    if (cutMin <= spanMin && cutMax < spanMax) {
      spans[spanOffset] = cutMax;
      dirtyMask[idx] = 1u;
      continue;
    }

    // Split case - simplified, just do bottom cut for now
    if (cutMin > spanMin && cutMax < spanMax) {
      spans[spanOffset + 1u] = cutMin;
      dirtyMask[idx] = 1u;
    }
  }

  // Update heightMap - kalan span'ların en yüksek noktası
  let finalSpanCount = spanCounts[idx];
  if (finalSpanCount == 0u) {
    heightMap[idx] = uniforms.bboxMinZ;
  } else {
    // spanMax = kalan malzemenin üst yüzeyi
    heightMap[idx] = spans[offset + 1u];
  }
}

@compute @workgroup_size(256)
fn extractHeightMap(@builtin(global_invocation_id) gid: vec3<u32>) {
  let idx = gid.x;
  let totalCells = uniforms.resX * uniforms.resY;

  if (idx >= totalCells) {
    return;
  }

  let spanCount = spanCounts[idx];
  let offset = idx * MAX_SPANS * SPAN_SIZE;

  if (spanCount == 0u) {
    heightMap[idx] = uniforms.bboxMinZ;
  } else {
    // Find maximum spanMax across all spans (top surface)
    var maxSpanMax = spans[offset + 1u];
    for (var s: u32 = 1u; s < spanCount; s++) {
      let spanMax = spans[offset + s * SPAN_SIZE + 1u];
      if (spanMax > maxSpanMax) {
        maxSpanMax = spanMax;
      }
    }
    heightMap[idx] = maxSpanMax;
  }
}
`;

export class DexelGPUEngine {
  private device: GPUDevice | null = null;
  private processPipeline: GPUComputePipeline | null = null;
  private heightMapPipeline: GPUComputePipeline | null = null;

  private uniformBuffer: GPUBuffer | null = null;
  private spansBuffer: GPUBuffer | null = null;
  private spanCountsBuffer: GPUBuffer | null = null;
  private heightMapBuffer: GPUBuffer | null = null;
  private dirtyMaskBuffer: GPUBuffer | null = null;
  private readbackBuffer: GPUBuffer | null = null;

  private bindGroup: GPUBindGroup | null = null;

  private config: DexelGPUConfig | null = null;
  private initialized = false;

  // CPU fallback data
  private cpuHeightMap: Float32Array | null = null;

  // Pre-allocated uniform data (ZERO allocation in hot path)
  private uniformData = new Float32Array(20);
  private uniformDataView = new DataView(this.uniformData.buffer);

  // Cached stock initialization data
  private stockSpansData: Float32Array | null = null;
  private stockSpanCountsData: Uint32Array | null = null;
  private stockDirtyData: Uint32Array | null = null;

  async init(config: DexelGPUConfig): Promise<boolean> {
    this.config = config;

    // Check WebGPU support
    if (!navigator.gpu) {
      console.warn('WebGPU not supported, falling back to CPU');
      return this.initCPUFallback();
    }

    try {
      const adapter = await navigator.gpu.requestAdapter();
      if (!adapter) {
        console.warn('No GPU adapter found, falling back to CPU');
        return this.initCPUFallback();
      }

      this.device = await adapter.requestDevice();

      // Create shader module
      const shaderModule = this.device.createShaderModule({
        code: DEXEL_COMPUTE_SHADER
      });

      // Create pipelines
      this.processPipeline = this.device.createComputePipeline({
        layout: 'auto',
        compute: {
          module: shaderModule,
          entryPoint: 'processToolPosition'
        }
      });

      this.heightMapPipeline = this.device.createComputePipeline({
        layout: 'auto',
        compute: {
          module: shaderModule,
          entryPoint: 'extractHeightMap'
        }
      });

      // Create buffers
      const totalCells = config.resX * config.resY;
      const spansSize = totalCells * MAX_SPANS * SPAN_SIZE * 4; // Float32

      this.uniformBuffer = this.device.createBuffer({
        size: 80, // 20 floats aligned (16-byte boundary)
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
      });

      this.spansBuffer = this.device.createBuffer({
        size: spansSize,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC
      });

      this.spanCountsBuffer = this.device.createBuffer({
        size: totalCells * 4, // Uint32
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC
      });

      this.heightMapBuffer = this.device.createBuffer({
        size: totalCells * 4, // Float32
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
      });

      this.dirtyMaskBuffer = this.device.createBuffer({
        size: totalCells * 4, // Uint32
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC
      });

      this.readbackBuffer = this.device.createBuffer({
        size: totalCells * 4,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST
      });

      // Create bind group
      this.bindGroup = this.device.createBindGroup({
        layout: this.processPipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.uniformBuffer } },
          { binding: 1, resource: { buffer: this.spansBuffer } },
          { binding: 2, resource: { buffer: this.spanCountsBuffer } },
          { binding: 3, resource: { buffer: this.heightMapBuffer } },
          { binding: 4, resource: { buffer: this.dirtyMaskBuffer } }
        ]
      });

      // Pre-allocate stock initialization data (cache)
      this.stockSpansData = new Float32Array(totalCells * MAX_SPANS * SPAN_SIZE);
      this.stockSpanCountsData = new Uint32Array(totalCells);
      this.stockDirtyData = new Uint32Array(totalCells);

      // Initialize stock
      await this.initializeStock();

      this.initialized = true;
      // DexelGPU initialized
      return true;

    } catch (err) {
      console.error('WebGPU init failed:', err);
      return this.initCPUFallback();
    }
  }

  private initCPUFallback(): boolean {
    if (!this.config) return false;
    const totalCells = this.config.resX * this.config.resY;
    this.cpuHeightMap = new Float32Array(totalCells);
    this.cpuHeightMap.fill(this.config.bbox.minZ);
    console.warn('DexelGPU using CPU fallback');
    return true;
  }

  private async initializeStock(): Promise<void> {
    if (!this.device || !this.config) return;

    const { resX, resY, bbox } = this.config;
    const totalCells = resX * resY;

    // Use cached buffers (no allocation)
    if (!this.stockSpansData || !this.stockSpanCountsData || !this.stockDirtyData) {
      console.error('Stock data buffers not allocated');
      return;
    }

    // Initialize spans - each cell has one span [minZ, maxZ]
    for (let i = 0; i < totalCells; i++) {
      const offset = i * MAX_SPANS * SPAN_SIZE;
      this.stockSpansData[offset] = bbox.minZ;
      this.stockSpansData[offset + 1] = bbox.maxZ;
      this.stockSpanCountsData[i] = 1;
    }

    this.device.queue.writeBuffer(this.spansBuffer!, 0, this.stockSpansData);
    this.device.queue.writeBuffer(this.spanCountsBuffer!, 0, this.stockSpanCountsData);

    // Clear dirty mask (already zeroed on first allocation, but reset for subsequent calls)
    this.stockDirtyData.fill(0);
    this.device.queue.writeBuffer(this.dirtyMaskBuffer!, 0, this.stockDirtyData);
  }

  processToolPosition(sample: GPUToolSample): void {
    if (!this.device || !this.config || !this.bindGroup) {
      return;
    }

    const { resX, resY, cellSize, bbox } = this.config;

    this.uniformData[0] = resX;
    this.uniformData[1] = resY;
    this.uniformData[2] = cellSize;
    this.uniformData[3] = bbox.minX;
    this.uniformData[4] = bbox.minY;
    this.uniformData[5] = bbox.minZ;
    this.uniformData[6] = bbox.maxZ;
    this.uniformData[7] = bbox.maxX;
    this.uniformData[8] = sample.x;
    this.uniformData[9] = sample.y;
    this.uniformData[10] = sample.z;
    this.uniformData[11] = sample.toolRadius;
    this.uniformData[12] = sample.toolType;
    this.uniformData[13] = sample.cornerRadius;
    this.uniformData[14] = sample.toolAxisI;
    this.uniformData[15] = sample.toolAxisJ;
    this.uniformData[16] = sample.toolAxisK;
    this.uniformData[17] = sample.toolLength;
    this.uniformData[18] = 0; // _pad0
    this.uniformData[19] = 0; // _pad1

    this.uniformDataView.setUint32(0, resX, true);
    this.uniformDataView.setUint32(4, resY, true);
    this.uniformDataView.setUint32(48, sample.toolType, true);

    this.device.queue.writeBuffer(this.uniformBuffer!, 0, this.uniformData);

    // Dispatch compute
    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(this.processPipeline!);
    pass.setBindGroup(0, this.bindGroup);
    pass.dispatchWorkgroups(
      Math.ceil(resX / 16),
      Math.ceil(resY / 16)
    );
    pass.end();

    this.device.queue.submit([encoder.finish()]);
  }

  async processBatch(samples: GPUToolSample[]): Promise<void> {
    if (!this.device || !this.config || !this.bindGroup || samples.length === 0) return;

    const { resX, resY } = this.config;
    const wgX = Math.ceil(resX / 16);
    const wgY = Math.ceil(resY / 16);
    const encoder = this.device.createCommandEncoder();

    for (const sample of samples) {
      this.writeUniformsForSample(sample);
      this.device.queue.writeBuffer(this.uniformBuffer!, 0, this.uniformData);

      const pass = encoder.beginComputePass();
      pass.setPipeline(this.processPipeline!);
      pass.setBindGroup(0, this.bindGroup);
      pass.dispatchWorkgroups(wgX, wgY);
      pass.end();
    }

    this.device.queue.submit([encoder.finish()]);
  }

  private writeUniformsForSample(sample: GPUToolSample): void {
    if (!this.config) return;

    const { resX, resY, cellSize, bbox } = this.config;

    this.uniformData[0] = resX;
    this.uniformData[1] = resY;
    this.uniformData[2] = cellSize;
    this.uniformData[3] = bbox.minX;
    this.uniformData[4] = bbox.minY;
    this.uniformData[5] = bbox.minZ;
    this.uniformData[6] = bbox.maxZ;
    this.uniformData[7] = bbox.maxX;
    this.uniformData[8] = sample.x;
    this.uniformData[9] = sample.y;
    this.uniformData[10] = sample.z;
    this.uniformData[11] = sample.toolRadius;
    this.uniformData[12] = sample.toolType;
    this.uniformData[13] = sample.cornerRadius;
    this.uniformData[14] = sample.toolAxisI;
    this.uniformData[15] = sample.toolAxisJ;
    this.uniformData[16] = sample.toolAxisK;
    this.uniformData[17] = sample.toolLength;
    this.uniformData[18] = 0; // _pad0
    this.uniformData[19] = 0; // _pad1

    this.uniformDataView.setUint32(0, resX, true);
    this.uniformDataView.setUint32(4, resY, true);
    this.uniformDataView.setUint32(48, sample.toolType, true);
  }

  async extractHeightMap(): Promise<Float32Array> {
    if (!this.device || !this.config || !this.bindGroup) {
      return this.cpuHeightMap || new Float32Array(0);
    }

    const { resX, resY } = this.config;
    const totalCells = resX * resY;

    // Run height map extraction
    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(this.heightMapPipeline!);
    pass.setBindGroup(0, this.bindGroup);
    pass.dispatchWorkgroups(Math.ceil(totalCells / 256));
    pass.end();

    // Copy to readback buffer
    encoder.copyBufferToBuffer(
      this.heightMapBuffer!, 0,
      this.readbackBuffer!, 0,
      totalCells * 4
    );

    this.device.queue.submit([encoder.finish()]);

    // Read back
    await this.readbackBuffer!.mapAsync(GPUMapMode.READ);
    const data = new Float32Array(this.readbackBuffer!.getMappedRange().slice(0));
    this.readbackBuffer!.unmap();

    return data;
  }

  async reset(): Promise<void> {
    await this.initializeStock();
  }

  dispose(): void {
    this.uniformBuffer?.destroy();
    this.spansBuffer?.destroy();
    this.spanCountsBuffer?.destroy();
    this.heightMapBuffer?.destroy();
    this.dirtyMaskBuffer?.destroy();
    this.readbackBuffer?.destroy();
    this.processPipeline = null;
    this.heightMapPipeline = null;
    this.bindGroup = null;
    this.device = null;
    this.initialized = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getResolution(): { resX: number; resY: number } {
    return this.config ? { resX: this.config.resX, resY: this.config.resY } : { resX: 0, resY: 0 };
  }

  getBBox() {
    return this.config?.bbox || { minX: 0, maxX: 0, minY: 0, maxY: 0, minZ: 0, maxZ: 0 };
  }
}

/**
 * Factory function - WebGPU desteklenmiyorsa null döner
 */
export async function createDexelGPUEngine(config: DexelGPUConfig): Promise<DexelGPUEngine | null> {
  const engine = new DexelGPUEngine();
  const success = await engine.init(config);
  return success ? engine : null;
}
